import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import * as jose from 'jose';

/**
 * Hook personnalisé pour gérer l'authentification et récupérer les informations utilisateur
 * @returns {Object} { token, shopId, isLoading, error, user }
 */
export const useAuth = () => {
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
  const [token, setToken] = useState('');
  const [shopId, setShopId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    const fetchAuthData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Récupérer le token
        const accessToken = await getAccessTokenSilently();
        setToken(accessToken);

        // Vérifier d'abord si tenant_id est disponible dans l'objet user
        // Auth0 peut stocker des custom claims dans user.app_metadata ou user.user_metadata
        let tenantId = null;

        // Vérifier dans user object (app_metadata, user_metadata, ou directement)
        if (user) {
          tenantId = 
            user.tenant_id || 
            user['https://zagogroupe.com/tenant_id'] || // Custom claim possible
            user.app_metadata?.tenant_id ||
            user.user_metadata?.tenant_id;
        }

        // Si pas trouvé dans user, décoder le JWT
        if (!tenantId) {
          const decodedToken = jose.decodeJwt(accessToken);
          tenantId = decodedToken.tenant_id || decodedToken['https://zagogroupe.com/tenant_id'];
        }

        if (!tenantId) {
          // Log pour debug - voir ce qui est disponible
          console.log('User object:', user);
          console.log('Decoded token:', jose.decodeJwt(accessToken));
          throw new Error('tenant_id non trouvé dans le token ou les métadonnées utilisateur');
        }

        setShopId(tenantId);
      } catch (err) {
        console.error('Erreur lors de la récupération des données d\'authentification:', err);
        setError(err.message || 'Erreur d\'authentification');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuthData();
  }, [isAuthenticated, getAccessTokenSilently, user]);

  return {
    token,
    shopId,
    isLoading,
    error,
    isAuthenticated,
    user, // Exposer user pour debug
  };
};

export default useAuth;
